/**
 * Chunked browser recorder (web edition of PRD §9).
 * - MediaRecorder emits a self-contained segment every CHUNK_MS.
 * - Each segment is persisted to IndexedDB before upload.
 * - Uploads are sequenced and retried with backoff; offline periods simply queue.
 * - Level metering via AnalyserNode drives the "voice is clear" UI.
 */
import { ref, uploadBytes } from "firebase/storage";
import { increment } from "firebase/firestore";
import { storage, usingEmulators } from "../lib/firebase";
import { currentUser } from "../data/auth";
import { updateMeeting, writeChunkMeta } from "../data/store";
import { queue, type QueuedChunk } from "./queue";

export const CHUNK_MS = 20_000;
const SILENCE_PEAK = 0.015; // RMS below this for a whole segment = no speech
const MAX_ATTEMPTS = 8;

export type LevelState = "clear" | "silent" | "loud";

export type CaptureSource = "mic" | "call";
/** Take the microphone right now, inside the tap. Returns null if the person says no. */
export async function grabMicrophone(): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  } catch {
    return null;
  }
}

/** Chrome/Edge/Arc can share a tab's audio through getDisplayMedia; Safari and Firefox cannot. */
export const canCaptureCall = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia && /Chrome\//.test(navigator.userAgent) && !/Mobile/.test(navigator.userAgent);

export interface RecorderEvents {
  onTick?: (elapsedSec: number) => void;
  /** The shared tab/window stopped sharing (user hit "Stop sharing" or closed the call); recording continues from the mic. */
  onCallEnded?: () => void;
  onLevel?: (rms: number, state: LevelState, bars: number[]) => void;
  onQueue?: (pending: number, uploaded: number) => void;
  onError?: (message: string) => void;
  /** Another app took the microphone (a call, usually). The timeline keeps running; nothing is heard. */
  onMicLost?: () => void;
  /** The microphone is ours again. */
  onMicBack?: () => void;
  /** The room has been quiet a while; `secondsLeft` is how long before Timo stops on its own. */
  onQuiet?: (quietSec: number, secondsLeft: number) => void;
  /** Long enough. Nothing was being said, so stop and write up whatever was captured. */
  onQuietStop?: () => void;
}

/** A room left recording itself costs real credit and produces notes about nothing. */
export const QUIET_WARN_SEC = 240;
export const QUIET_STOP_SEC = 300;
/** Float RMS, about -48 dBFS: above a laptop microphone's own hiss, well below speech across a table. */
const QUIET_LEVEL = 0.004;

/**
 * Loudness, measured on the audio thread.
 *
 * The page's own timers are throttled to about once a second when its tab is in the background -
 * which is exactly where Timo's tab is during a call in Meet or Zoom - and a sampler that wakes that
 * rarely mostly lands in the gaps between words. That is how a live conversation came to be cut off
 * as silence three minutes in. The audio thread is never throttled, so it does the listening.
 */
const LEVEL_WORKLET = `
class TimoLevel extends AudioWorkletProcessor {
  constructor() { super(); this.peak = 0; this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      let sum = 0; for (let i = 0; i < ch.length; i++) sum += ch[i] * ch[i];
      const rms = Math.sqrt(sum / ch.length);
      if (rms > this.peak) this.peak = rms;
      // About ten reports a second: the loudest block in each tenth.
      if (++this.n >= 37) { this.port.postMessage(this.peak); this.peak = 0; this.n = 0; }
    }
    return true;
  }
}
registerProcessor("timo-level", TimoLevel);`;

function pickMime() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return candidates.find((m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) ?? "";
}

export class ChunkedRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private segmentStart = 0;
  private seq = 0;
  private startedAt = 0;
  private pausedTotal = 0;
  private pausedAt = 0;
  private tickTimer: number | undefined;
  private levelTimer: number | undefined;
  private quietStopped = false;
  private uploading = false;
  private uploaded = 0;
  private silentSince = 0;
  /** When something louder than a quiet room was last heard. Drives the auto-stop. */
  private lastSound = Date.now();
  /** True once the audio-thread meter is running; until then the page's sampler stands in. */
  private threadMeter = false;
  synthetic = false;
  private segmentPeak = 0; // highest RMS seen in the current segment; used for silence gating
  skippedSilent = 0;
  source: CaptureSource = "mic";
  micMuted = false;
  private callStream: MediaStream | null = null;
  private micStream: MediaStream | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  private micLost = false;
  readonly mime = pickMime();

  constructor(readonly meetingId: string, private events: RecorderEvents = {}) {}

  get elapsedSec() {
    if (!this.startedAt) return 0;
    const paused = this.pausedAt ? Date.now() - this.pausedAt : 0;
    return Math.max(0, Math.floor((Date.now() - this.startedAt - this.pausedTotal - paused) / 1000));
  }

  async start(source: CaptureSource = "mic", preAcquired?: MediaStream | null) {
    if (!this.mime) throw new Error("This browser cannot record audio. Try Chrome, Edge or Safari 17+.");
    this.audioCtx = new AudioContext();
    let mic: MediaStream;
    try {
      // Safari on iOS only grants the microphone inside the tap that asked for it. Anything awaited
      // first, such as creating the meeting, spends that permission, so the caller can hand us a
      // stream it took during the tap instead.
      mic = preAcquired ?? await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (err) {
      if (!usingEmulators) throw err;
      // Emulator-only stand-in microphone: a quiet synthesized tone so the whole capture → upload →
      // transcribe → summarize loop can be exercised where no real microphone is available.
      mic = syntheticMicrophone(this.audioCtx);
      this.synthetic = true;
    }
    if (source === "call") {
      // The other side of an online call: the user picks the Meet/Zoom/Teams tab and ticks "Share tab audio".
      // Chrome insists on a video track in the request; we drop it immediately and keep only the audio.
      let shared: MediaStream;
      try {
        shared = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }, selfBrowserSurface: "exclude", systemAudio: "include", surfaceSwitching: "include" } as DisplayMediaStreamOptions);
      } catch (err) {
        mic.getTracks().forEach((t) => t.stop());
        throw new Error((err as Error).name === "NotAllowedError" ? "Call capture cancelled" : (err as Error).message);
      }
      shared.getVideoTracks().forEach((t) => t.stop());
      const callTrack = shared.getAudioTracks()[0];
      if (!callTrack) {
        mic.getTracks().forEach((t) => t.stop());
        throw new Error("No call audio was shared. Pick the meeting tab and tick Share tab audio (or Share system audio for a window).");
      }
      this.callStream = new MediaStream([callTrack]);
      callTrack.onended = () => { this.callStream = null; this.source = "mic"; this.events.onCallEnded?.(); };
      this.source = "call";
    }
    // Mix every source into one stream so each segment carries both sides of the conversation.
    const dest = this.audioCtx.createMediaStreamDestination();
    this.dest = dest;
    this.audioCtx.createMediaStreamSource(mic).connect(dest);
    if (this.callStream) this.audioCtx.createMediaStreamSource(this.callStream).connect(dest);
    this.micStream = mic;
    this.watchMic(mic);
    document.addEventListener("visibilitychange", this.onVisible);
    this.stream = dest.stream;
    this.analyser = this.audioCtx.createAnalyser();
    // 2048 samples is about 43ms - a syllable, not a sliver of one.
    this.analyser.fftSize = 2048;
    this.audioCtx.createMediaStreamSource(dest.stream).connect(this.analyser);
    this.lastSound = Date.now();
    void this.startThreadMeter(dest.stream);

    this.startedAt = Date.now();
    this.spawnSegmentRecorder();
    this.tickTimer = window.setInterval(() => this.events.onTick?.(this.elapsedSec), 1000);
    this.levelTimer = window.setInterval(() => this.sampleLevel(), 120);
    window.addEventListener("online", this.drain);
    void this.drain();
  }

  /**
   * A fresh MediaRecorder per segment guarantees each blob has its own container header,
   * so every chunk is independently decodable by the transcription worker.
   */
  private spawnSegmentRecorder() {
    if (!this.stream) return;
    const rec = new MediaRecorder(this.stream, { mimeType: this.mime, audioBitsPerSecond: 48_000 });
    this.segmentStart = this.elapsedSec;
    const parts: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size && parts.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(parts, { type: this.mime });
      const peak = this.segmentPeak;
      this.segmentPeak = 0;
      if (blob.size > 0) {
        // Silence gate: a segment whose loudest sample never rose above the noise floor carries no speech.
        // Record its metadata so the timeline stays continuous, but never upload or transcribe it.
        if (peak < SILENCE_PEAK && !this.synthetic) void this.skipSilent(this.segmentStart, this.elapsedSec - this.segmentStart, blob.size);
        else void this.enqueue(blob, this.segmentStart, this.elapsedSec - this.segmentStart);
      }
      if (this.recorder === rec && rec.state === "inactive" && this.startedAt && !this.pausedAt && !this.stopping) this.spawnSegmentRecorder();
    };
    rec.start();
    this.recorder = rec;
    window.setTimeout(() => rec.state === "recording" && rec.stop(), CHUNK_MS);
  }

  private stopping = false;

  /** Mute your microphone mid-meeting. The timeline keeps running (and call audio keeps recording). */
  setMicMuted(muted: boolean) {
    this.micMuted = muted;
    this.micStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  /**
   * A phone hands the microphone to a call app the moment a call starts, and to nobody else until it
   * ends. Safari and Chrome report that as the track going mute (or ending outright). Timo keeps the
   * meeting open, tells the person, and takes the microphone back the moment it is free again.
   */
  private watchMic(mic: MediaStream) {
    const track = mic.getAudioTracks()[0];
    if (!track) return;
    track.onmute = () => { if (!this.micLost) { this.micLost = true; this.events.onMicLost?.(); } };
    track.onunmute = () => { if (this.micLost) { this.micLost = false; void this.audioCtx?.resume(); this.events.onMicBack?.(); } };
    track.onended = () => { if (!this.micLost) { this.micLost = true; this.events.onMicLost?.(); } void this.reacquireMic(); };
  }

  private onVisible = () => {
    if (document.visibilityState !== "visible" || !this.startedAt || this.stopping) return;
    // Coming back from the call app: the audio context was interrupted, and the track may be dead.
    void this.audioCtx?.resume();
    const track = this.micStream?.getAudioTracks()[0];
    if (this.micLost || !track || track.readyState === "ended") void this.reacquireMic();
  };

  private reacquiring = false;
  private async reacquireMic() {
    if (this.reacquiring || !this.audioCtx || !this.dest || this.stopping) return;
    this.reacquiring = true;
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      this.micStream?.getTracks().forEach((t) => t.stop());
      fresh.getAudioTracks().forEach((t) => (t.enabled = !this.micMuted));
      this.audioCtx.createMediaStreamSource(fresh).connect(this.dest);
      this.micStream = fresh;
      this.watchMic(fresh);
      await this.audioCtx.resume();
      if (this.micLost) { this.micLost = false; this.events.onMicBack?.(); }
    } catch {
      // Still held by the call. The next return to the app tries again.
    } finally {
      this.reacquiring = false;
    }
  }

  get microphoneLost() { return this.micLost; }

  pause() {
    if (this.pausedAt) return;
    this.pausedAt = Date.now();
    if (this.recorder?.state === "recording") this.recorder.stop();
  }

  resume() {
    this.lastSound = Date.now();
    if (!this.pausedAt) return;
    this.pausedTotal += Date.now() - this.pausedAt;
    this.pausedAt = 0;
    this.spawnSegmentRecorder();
  }

  async stop() {
    this.stopping = true;
    window.clearInterval(this.tickTimer);
    window.clearInterval(this.levelTimer);
    window.removeEventListener("online", this.drain);
    document.removeEventListener("visibilitychange", this.onVisible);
    const done = new Promise<void>((resolve) => {
      if (!this.recorder || this.recorder.state !== "recording") return resolve();
      const prev = this.recorder.onstop;
      this.recorder.onstop = (e) => {
        prev?.call(this.recorder!, e);
        resolve();
      };
      this.recorder.stop();
    });
    await done;
    [this.stream, this.micStream, this.callStream].forEach((st) => st?.getTracks().forEach((t) => t.stop()));
    await this.audioCtx?.close();
    await this.drain();
    return { elapsedSec: this.elapsedSec, chunkCount: this.seq };
  }

  private async skipSilent(offsetSec: number, durationSec: number, bytes: number) {
    const seq = this.seq++;
    this.skippedSilent++;
    await writeChunkMeta(this.meetingId, seq, { state: "silent", offsetSec, durationSec, bytes, mime: this.mime }).catch(() => {});
    // Count it as transcribed so finalization never waits on it.
    await updateMeeting(this.meetingId, { chunkCount: this.seq, durationSec: this.elapsedSec, transcribedCount: increment(1), silentChunks: increment(1) }).catch(() => {});
    this.notifyQueue();
  }

  private async enqueue(blob: Blob, offsetSec: number, durationSec: number) {
    const seq = this.seq++;
    const chunk: QueuedChunk = {
      key: `${this.meetingId}:${seq}`,
      meetingId: this.meetingId,
      seq,
      blob,
      mime: this.mime,
      offsetSec,
      durationSec,
      state: "queued",
      attempts: 0,
      createdAt: Date.now(),
    };
    await queue.put(chunk); // durable before any network
    await writeChunkMeta(this.meetingId, seq, { state: "queued", offsetSec, durationSec, bytes: blob.size, mime: this.mime }).catch(() => {});
    await updateMeeting(this.meetingId, { chunkCount: this.seq, durationSec: this.elapsedSec }).catch(() => {});
    this.notifyQueue();
    void this.drain();
  }

  private drain = async () => {
    if (this.uploading) return;
    this.uploading = true;
    try {
      for (const chunk of await queue.pending(this.meetingId)) {
        if (!navigator.onLine) break;
        if (chunk.attempts >= MAX_ATTEMPTS) continue;
        await queue.setState(chunk.key, "uploading");
        try {
          await this.upload(chunk);
          await queue.remove(chunk.key); // safe-delete only after ack (W4-07)
          this.uploaded++;
          await writeChunkMeta(chunk.meetingId, chunk.seq, { state: "uploaded" }).catch(() => {});
        } catch (err) {
          const attempts = chunk.attempts + 1;
          await queue.setState(chunk.key, "failed", attempts);
          this.events.onError?.(`Chunk ${chunk.seq} upload failed (attempt ${attempts}). Will retry.`);
          window.setTimeout(() => void this.drain(), Math.min(60_000, 1000 * 2 ** attempts));
          break;
        }
        this.notifyQueue();
      }
    } finally {
      this.uploading = false;
      this.notifyQueue();
    }
  };

  private async upload(chunk: QueuedChunk) {
    const uid = currentUser()?.uid;
    if (!uid) throw new Error("Not signed in");
    const ext = chunk.mime.includes("mp4") ? "m4a" : chunk.mime.includes("ogg") ? "ogg" : "webm";
    const path = `meetings/${uid}/${chunk.meetingId}/chunks/${String(chunk.seq).padStart(5, "0")}.${ext}`;
    await uploadBytes(ref(storage, path), chunk.blob, {
      contentType: chunk.mime.split(";")[0],
      customMetadata: { seq: String(chunk.seq), offsetSec: String(chunk.offsetSec), durationSec: String(chunk.durationSec), meetingId: chunk.meetingId, uid },
    });
  }

  private async notifyQueue() {
    const pending = (await queue.pending(this.meetingId)).length;
    this.events.onQueue?.(pending, this.uploaded);
  }

  private async startThreadMeter(stream: MediaStream) {
    try {
      const url = URL.createObjectURL(new Blob([LEVEL_WORKLET], { type: "application/javascript" }));
      await this.audioCtx!.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      const node = new AudioWorkletNode(this.audioCtx!, "timo-level");
      node.port.onmessage = (e: MessageEvent<number>) => { if (e.data > QUIET_LEVEL) this.lastSound = Date.now(); };
      this.audioCtx!.createMediaStreamSource(stream).connect(node);
      this.threadMeter = true;
    } catch {
      // No worklet (an old browser): the sampler below carries on alone, and while the tab is
      // hidden - where it cannot be trusted - it does not count quiet time at all.
      this.threadMeter = false;
    }
  }

  /** "Keep recording" on the warning: whatever the room is doing, start the quiet clock again. */
  keepListening() { this.lastSound = Date.now(); this.quietStopped = false; }

  private sampleLevel() {
    if (!this.analyser || this.pausedAt) return;
    // Float, not bytes: byte samples round anything quieter than about 0.008 to nothing, and the
    // old line between "silent" and "talking" sat at 0.01 - right on top of that floor.
    const buf = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (const v of buf) sum += v * v;
    const rms = Math.sqrt(sum / buf.length);
    if (rms > this.segmentPeak) this.segmentPeak = rms;
    const now = Date.now();
    if (rms > QUIET_LEVEL) this.lastSound = now;
    // Things that are quiet on purpose are not silence: a muted microphone with no call audio
    // behind it, and a hidden tab when nothing on the audio thread is listening.
    if ((this.micMuted && !this.callStream) || (!this.threadMeter && document.hidden)) this.lastSound = now;
    if (rms < QUIET_LEVEL) {
      if (!this.silentSince) this.silentSince = now;
    } else this.silentSince = 0;
    const state: LevelState = rms > 0.6 ? "loud" : this.silentSince && now - this.silentSince > 8000 ? "silent" : "clear";
    const bars = Array.from({ length: 12 }, (_, i) => Math.max(3, Math.min(20, rms * 60 * (0.6 + Math.sin(i * 1.7 + now / 90) * 0.4))));
    this.events.onLevel?.(rms, state, bars);

    // Warn before stopping. Someone thinking in silence should be asked, not cut off.
    const quietSec = Math.floor((now - this.lastSound) / 1000);
    if (quietSec >= QUIET_STOP_SEC) {
      if (!this.quietStopped) { this.quietStopped = true; this.events.onQuietStop?.(); }
    } else if (quietSec >= QUIET_WARN_SEC) {
      this.events.onQuiet?.(quietSec, QUIET_STOP_SEC - quietSec);
    } else if (this.quietStopped) {
      this.quietStopped = false;
    }
  }
}

function syntheticMicrophone(ctx: AudioContext): MediaStream {
  const dest = ctx.createMediaStreamDestination();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = 220;
  gain.gain.value = 0.05;
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.frequency.value = 0.5;
  lfoGain.gain.value = 0.04;
  lfo.connect(lfoGain).connect(gain.gain);
  osc.connect(gain).connect(dest);
  osc.start();
  lfo.start();
  return dest.stream;
}

/** Resume uploads for chunks left behind by a previous session (crash/reload recovery). */
export async function recoverPendingUploads() {
  const pending = await queue.pending();
  const byMeeting = new Map<string, number>();
  for (const c of pending) byMeeting.set(c.meetingId, (byMeeting.get(c.meetingId) ?? 0) + 1);
  for (const meetingId of byMeeting.keys()) {
    const r = new ChunkedRecorder(meetingId);
    await (r as unknown as { drain: () => Promise<void> }).drain();
  }
  return pending.length;
}
